import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different marker types
const createCustomIcon = (type) => {
  return L.icon({
    iconUrl: type === 'user' 
      ? 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png'
      : 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });
};

const Map = ({ 
  center = [20.5937, 78.9629], // Default center of India
  zoom = 5,
  markers = [],
  onLocationSelect,
  height = '400px',
  width = '100%',
  isSelectable = false,
  showDistanceCircle = false,
  userLocation = null
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current).setView(center, zoom);
    mapInstanceRef.current = map;

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    // Add location control
    const locationControl = L.control({ position: 'topleft' });
    locationControl.onAdd = function() {
      const div = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
      div.innerHTML = `
        <a href="#" title="Locate me" style="background-color: white; padding: 6px 8px; display: block;">
          <i class="fa fa-location-arrow" style="color: #007bff;"></i>
        </a>
      `;
      div.onclick = function(e) {
        e.preventDefault();
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              map.setView([latitude, longitude], 13);
              if (onLocationSelect) {
                onLocationSelect({ latitude, longitude });
              }
            },
            (error) => {
              console.error('Error getting location:', error);
            }
          );
        }
      };
      return div;
    };
    locationControl.addTo(map);

    // Add click handler if map is selectable
    if (isSelectable) {
      map.on('click', (e) => {
        const { lat, lng } = e.latlng;
        if (onLocationSelect) {
          onLocationSelect({ latitude: lat, longitude: lng });
        }
      });
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [center, isSelectable, onLocationSelect, zoom]);

  // Update markers and circle
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(marker);
      }
    });
    markersRef.current = [];

    // Clear existing circle
    if (circleRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(circleRef.current);
      circleRef.current = null;
    }

    // Add new markers
    markers.forEach(marker => {
      if (marker.latitude && marker.longitude) {
        const icon = createCustomIcon(marker.type || 'ad');
        const newMarker = L.marker([marker.latitude, marker.longitude], { icon })
          .addTo(mapInstanceRef.current)
          .bindPopup(marker.popup || 'Location');
        markersRef.current.push(newMarker);
      }
    });

    // Add distance circle if user location is provided
    if (showDistanceCircle && userLocation && userLocation.latitude && userLocation.longitude) {
      circleRef.current = L.circle([userLocation.latitude, userLocation.longitude], {
        color: 'blue',
        fillColor: '#30f',
        fillOpacity: 0.1,
        radius: 10000 // 10km radius
      }).addTo(mapInstanceRef.current);
    }
  }, [markers, userLocation, showDistanceCircle]);

  return (
    <div className="map-container">
      <div 
        ref={mapRef} 
        style={{ 
          height, 
          width,
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}
      />
      {isSelectable && (
        <div className="map-instructions mt-2 text-sm text-gray-600">
          Click on the map to select a location or use the location button in the top-left corner
        </div>
      )}
    </div>
  );
};

export default Map; 